import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminSettingsAPI } from '../services/api';
import { Save, Map, ArrowLeft, Navigation } from 'lucide-react';

interface DistanceRanges {
  firstRange: number;
  secondRange: number;
  thirdRange: number;
  showAllRange: number;
}

const AdminDistanceRangesPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [distanceRanges, setDistanceRanges] = useState<DistanceRanges>({
    firstRange: 5,
    secondRange: 10,
    thirdRange: 20,
    showAllRange: 50,
  });

  useEffect(() => {
    // Check if user is admin
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    if (user) {
      fetchDistanceRanges();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const fetchDistanceRanges = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await adminSettingsAPI.getSettings();
      
      if (response.data && response.data.distanceRanges) {
        setDistanceRanges(response.data.distanceRanges);
      }
    } catch (err: any) {
      console.error('Error fetching distance ranges:', err);
      setError('Failed to load distance ranges');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage('');
      setError('');

      // Validate ranges
      if (distanceRanges.firstRange <= 0 || 
          distanceRanges.secondRange <= distanceRanges.firstRange ||
          distanceRanges.thirdRange <= distanceRanges.secondRange ||
          distanceRanges.showAllRange <= distanceRanges.thirdRange) {
        setError('Distance ranges must be in ascending order and greater than 0');
        return;
      }

      await adminSettingsAPI.updateDistanceRanges(distanceRanges);
      setMessage('Distance ranges updated successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      console.error('Error saving distance ranges:', err);
      setError(err.response?.data?.message || 'Failed to save distance ranges');
    } finally {
      setSaving(false);
    }
  };

  const handleRangeChange = (field: keyof DistanceRanges, value: string) => {
    const numValue = parseFloat(value) || 0;
    setDistanceRanges(prev => ({
      ...prev,
      [field]: numValue,
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
          <p className="mt-4 text-gray-300">Loading distance ranges...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="flex items-center text-black hover:text-gray-800 mb-4"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Dashboard
          </button>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center mb-2">
                <Map className="h-8 w-8 text-black mr-3" />
                <h1 className="text-3xl font-bold text-white">Distance Ranges Management</h1>
              </div>
              <p className="text-gray-600">Configure progressive search ranges for finding drivers</p>
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-5 w-5 mr-2" />
                  Save
                </>
              )}
            </button>
          </div>
        </div>

        {/* Messages */}
        {message && (
          <div className="mb-6 bg-green-50 border-l-4 border-green-500 p-4 rounded">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-700">{message}</p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Distance Ranges Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center mb-4">
            <Navigation className="h-5 w-5 text-black mr-2" />
            <h2 className="text-xl font-semibold text-gray-900">Search Distance Ranges</h2>
          </div>
          <p className="text-gray-600 mb-6">
            Configure the progressive search ranges for finding drivers. The system will search in expanding circles:
            first range, then second, then third, and finally show all drivers within the "Show All" range.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* First Range */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                  <div className="w-6 h-6 bg-blue-600 rounded-full"></div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">First Range</h3>
                  <p className="text-sm text-gray-600">Initial search radius</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Distance (km)
                </label>
                <input
                  type="number"
                  value={distanceRanges.firstRange}
                  onChange={(e) => handleRangeChange('firstRange', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                  placeholder="5"
                  step="0.1"
                />
                <p className="text-xs text-gray-500 mt-1">Minimum search radius in kilometers</p>
              </div>
            </div>

            {/* Second Range */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-3">
                  <div className="w-6 h-6 bg-green-600 rounded-full"></div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Second Range</h3>
                  <p className="text-sm text-gray-600">Expanded search radius</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Distance (km)
                </label>
                <input
                  type="number"
                  value={distanceRanges.secondRange}
                  onChange={(e) => handleRangeChange('secondRange', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                  placeholder="10"
                  step="0.1"
                />
                <p className="text-xs text-gray-500 mt-1">Must be greater than first range</p>
              </div>
            </div>

            {/* Third Range */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
                  <div className="w-6 h-6 bg-yellow-600 rounded-full"></div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Third Range</h3>
                  <p className="text-sm text-gray-600">Further expanded search</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Distance (km)
                </label>
                <input
                  type="number"
                  value={distanceRanges.thirdRange}
                  onChange={(e) => handleRangeChange('thirdRange', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                  placeholder="20"
                  step="0.1"
                />
                <p className="text-xs text-gray-500 mt-1">Must be greater than second range</p>
              </div>
            </div>

            {/* Show All Range */}
            <div className="bg-pink-50 border border-pink-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mr-3">
                  <Navigation className="h-6 w-6 text-pink-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Show All Range</h3>
                  <p className="text-sm text-gray-600">Maximum search radius</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Distance (km)
                </label>
                <input
                  type="number"
                  value={distanceRanges.showAllRange}
                  onChange={(e) => handleRangeChange('showAllRange', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                  placeholder="50"
                  step="0.1"
                />
                <p className="text-xs text-gray-500 mt-1">Maximum search radius - shows all drivers within this range</p>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="bg-white border border-gray-300 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-black mb-4">💡 How It Works</h3>
          <ul className="space-y-2 text-gray-800">
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>The system searches for drivers in progressive circles</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>First, it searches within the <strong>First Range</strong></span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>If no drivers found, expands to <strong>Second Range</strong></span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Then expands to <strong>Third Range</strong> if still no drivers</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Finally shows all drivers within <strong>Show All Range</strong></span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Ranges must be in ascending order (First {'<'} Second {'<'} Third {'<'} Show All)</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminDistanceRangesPage;

